import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { primaryNavLinks, socialLinks, ROUTES } from '../data/siteLinks';

const useIsMobile = (breakpoint = 768) => {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth <= breakpoint : false,
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);
  return isMobile;
};

const Header: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const isMobile = useIsMobile();

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileOpen) {
      return undefined;
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setMobileOpen(false);
      }
    };

    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleEscape);

    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleEscape);
    };
  }, [mobileOpen]);

  const toggleMobile = () => setMobileOpen((prev) => !prev);
  const closeMobile = () => setMobileOpen(false);

  const styles: Record<string, React.CSSProperties> = {
    header: {
      position: 'sticky',
      top: 0,
      zIndex: 1000,
      backgroundColor: '#ffffff',
    },
    headerInner: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      maxWidth: '1040px',
      margin: '0 auto',
      padding: '28px 24px 14px',
      gap: '20px',
    },
    logo: {
      display: 'flex',
      alignItems: 'center',
      textDecoration: 'none',
      flexShrink: 0,
    },
    logoImg: {
      width: '290px',
      height: '111px',
      display: 'block',
      objectFit: 'contain',
    },
    nav: {
      display: 'flex',
      alignItems: 'center',
      gap: '2px',
      flex: 1,
      justifyContent: 'center',
      minWidth: 0,
      whiteSpace: 'nowrap',
    },
    rightSection: {
      display: 'flex',
      alignItems: 'center',
      gap: '2px',
      flexShrink: 0,
    },
    emailIcon: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'rgb(36, 35, 35)',
      textDecoration: 'none',
      padding: '6px',
      marginRight: '2px',
      borderRadius: '999px',
    },
    socialIcon: {
      width: '22px',
      height: '22px',
      display: 'block',
    },
    socialLink: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      textDecoration: 'none',
      padding: '4px',
      borderRadius: '999px',
      color: 'rgb(36, 35, 35)',
    },
    menuBtn: {
      display: 'none',
      flexDirection: 'column' as const,
      gap: '5px',
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      padding: '8px',
    },
    menuLine: {
      width: '24px',
      height: '2px',
      backgroundColor: 'rgb(36, 35, 35)',
      transition: 'all 0.3s',
    },
    mobileNav: {
      position: 'fixed' as const,
      top: '153px',
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: '#fff',
      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
      display: 'flex',
      flexDirection: 'column' as const,
      padding: '20px',
      zIndex: 999,
      overflowY: 'auto' as const,
    },
    mobileNavLink: {
      textDecoration: 'none',
      color: 'rgb(36, 35, 35)',
      fontSize: '16px',
      padding: '12px 0',
      borderBottom: '1px solid #eee',
    },
    mobileSocial: {
      display: 'flex',
      gap: '16px',
      paddingTop: '16px',
    },
  };

  return (
    <>
      <header style={styles.header}>
        <div style={styles.headerInner}>
          <Link to={ROUTES.home} style={styles.logo} onClick={closeMobile} aria-label="Ana sayfa">
            <img
              src="/images/logo.png"
              srcSet="/images/logo.png 1x, /images/logo@2x.png 2x, /images/logo@3x.png 3x"
              alt="Ayşegül Saraç"
              className="header-logo-img"
              style={styles.logoImg}
            />
          </Link>

          <nav style={styles.nav}>
            {primaryNavLinks.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`header-nav-link${location.pathname === item.path ? ' active' : ''}`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          <div style={styles.rightSection}>
            {!isMobile && (
              <a
                href="mailto:info@aysegulsarac.com?subject=Ay%C5%9Feg%C3%BCl'e%20bir%20sorum%20var%20%F0%9F%92%9A"
                style={styles.emailIcon}
                className="header-email"
                title="info@aysegulsarac.com"
                aria-label="E-posta gönder"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="4" width="20" height="16" rx="2"/>
                  <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
                </svg>
              </a>
            )}

            {!isMobile && (
              <span className="header-social-icons" style={{ display: 'inline-flex', alignItems: 'center', gap: '2px' }}>
                <a href={socialLinks.linkedin} target="_blank" rel="noopener noreferrer" style={styles.socialLink} aria-label="LinkedIn">
                  <svg style={styles.socialIcon} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14zM8.34 18.34V10.5H5.67v7.84h2.67zM7 9.32a1.55 1.55 0 1 0 0-3.1 1.55 1.55 0 0 0 0 3.1zm11.34 9.02v-4.3c0-2.48-1.32-3.63-3.09-3.63-1.43 0-2.07.79-2.43 1.34V10.5h-2.67c.04.75 0 7.84 0 7.84h2.67v-4.38c0-.24.02-.48.09-.65.19-.48.63-.97 1.36-.97.96 0 1.34.73 1.34 1.8v4.2h2.73z"/>
                  </svg>
                </a>

                <a href={socialLinks.instagram} target="_blank" rel="noopener noreferrer" style={styles.socialLink} aria-label="Instagram">
                  <svg style={styles.socialIcon} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
                    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
                    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
                  </svg>
                </a>

                <a href={socialLinks.youtube} target="_blank" rel="noopener noreferrer" style={styles.socialLink} aria-label="YouTube">
                  <svg style={styles.socialIcon} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M23.5 6.2a3 3 0 0 0-2.1-2.1C19.5 3.6 12 3.6 12 3.6s-7.5 0-9.4.5A3 3 0 0 0 .5 6.2C0 8.1 0 12 0 12s0 3.9.5 5.8a3 3 0 0 0 2.1 2.1c1.9.5 9.4.5 9.4.5s7.5 0 9.4-.5a3 3 0 0 0 2.1-2.1c.5-1.9.5-5.8.5-5.8s0-3.9-.5-5.8zM9.6 15.6V8.4l6.2 3.6-6.2 3.6z"/>
                  </svg>
                </a>
              </span>
            )}

            <button
              style={{
                ...styles.menuBtn,
                display: undefined,
              }}
              className={`header-menu-btn${mobileOpen ? ' open' : ''}`}
              onClick={toggleMobile}
              aria-label={mobileOpen ? 'Menüyü kapat' : 'Menüyü aç'}
              aria-expanded={mobileOpen}
              aria-controls="mobile-navigation"
              type="button"
            >
              <span style={styles.menuLine}></span>
              <span style={styles.menuLine}></span>
              <span style={styles.menuLine}></span>
            </button>
          </div>
        </div>
      </header>

      {mobileOpen && (
        <div id="mobile-navigation" style={styles.mobileNav} role="dialog" aria-modal="true" aria-label="Mobil navigasyon">
          {primaryNavLinks.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={closeMobile}
              style={
                location.pathname === item.path
                  ? { ...styles.mobileNavLink, color: 'rgb(16, 190, 132)', fontWeight: 600 }
                  : styles.mobileNavLink
              }
            >
              {item.label}
            </Link>
          ))}
          <div style={styles.mobileSocial}>
            <a href={socialLinks.linkedin} target="_blank" rel="noopener noreferrer" style={styles.socialLink} aria-label="LinkedIn">
              <svg style={styles.socialIcon} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14zM8.34 18.34V10.5H5.67v7.84h2.67zM7 9.32a1.55 1.55 0 1 0 0-3.1 1.55 1.55 0 0 0 0 3.1zm11.34 9.02v-4.3c0-2.48-1.32-3.63-3.09-3.63-1.43 0-2.07.79-2.43 1.34V10.5h-2.67c.04.75 0 7.84 0 7.84h2.67v-4.38c0-.24.02-.48.09-.65.19-.48.63-.97 1.36-.97.96 0 1.34.73 1.34 1.8v4.2h2.73z"/>
              </svg>
            </a>
            <a href={socialLinks.instagram} target="_blank" rel="noopener noreferrer" style={styles.socialLink} aria-label="Instagram">
              <svg style={styles.socialIcon} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
                <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
                <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
              </svg>
            </a>
            <a href={socialLinks.youtube} target="_blank" rel="noopener noreferrer" style={styles.socialLink} aria-label="YouTube">
              <svg style={styles.socialIcon} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M23.5 6.2a3 3 0 0 0-2.1-2.1C19.5 3.6 12 3.6 12 3.6s-7.5 0-9.4.5A3 3 0 0 0 .5 6.2C0 8.1 0 12 0 12s0 3.9.5 5.8a3 3 0 0 0 2.1 2.1c1.9.5 9.4.5 9.4.5s7.5 0 9.4-.5a3 3 0 0 0 2.1-2.1c.5-1.9.5-5.8.5-5.8s0-3.9-.5-5.8zM9.6 15.6V8.4l6.2 3.6-6.2 3.6z"/>
              </svg>
            </a>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
